import moment, { Moment } from "moment-timezone";
import { Form, RadioChangeEvent } from "antd";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RSAEncryption } from "../../../util/Cryptography";
import { useSelector } from "react-redux";

import { RootState } from "../../../appRedux/store";
import * as maintenanceNotificationService from "../../../services/maintenanceNotificationService";
import { CustomNotification } from "../../../util/CustomNotification";
import useGetOneMasterList from "../../../hooks/useGetOneMasterList";
import { ModuleAndIdMapping, Modules } from "../../../constants/DatabaseConstants/MasterConstant";
import useValue from "../../../hooks/useValue";
import { RouteConstants } from "../../../constants/ApplicationConstants/RouteConstants";
import { NotificationTypeConstants } from "../../../constants/DatabaseConstants/ParkingConstants";
import { MessagingSetOptions, MessagingTemplateConstants, notificationEndpointOptions, scheduleOptions, userOptions } from "../../../constants/ApplicationConstants/NotificationConstant";

import type { SubmitDataProps } from "../../../shared/interface/notification.interface";
import type { CheckboxValueType } from "antd/lib/checkbox/Group";
import type { RangePickerProps } from "antd/lib/date-picker";

type MaintenanceNotificationStateType = {
  [key in MessagingTemplateConstants]?: string;
};

export const useCreateNotification = () => {
  /* CONSTANTS */
  const navigate = useNavigate();
  const [form] = Form.useForm();

  /* STATE */
  const [scheduleType, setScheduleType] = useState<string>(scheduleOptions?.SCHEDULE_NOW);
  const [isPUSHSelected, setIsPUSHSelected] = useState(true);
  const [isSMSSelected, setIsSMSSelected] = useState(false);
  const [maintenanceNotification, setMaintenanceNotification] = useState<MaintenanceNotificationStateType>({});

  /* HOOKS */
  const { masterFilters, locationList } = useSelector(({ common }: RootState) => common);
  const { loading: notificationListLoading, masterList: notificationList } = useGetOneMasterList(ModuleAndIdMapping[Modules.NOTIFICATION_MASTER]);
  const { value: saving, handleEnableValue: handleEnableSaving, handleDisableValue: handleDisableSaving } = useValue(false);
  const { value: isMaintenanceSelected, handleValue: handleMaintenanceSelected } = useValue(false);

  /* HELPER */
  const disabledDate = (current: moment.Moment) => {
    return current && current < moment().startOf("day");
  };

  const disabledTime = (current: moment.Moment | null) => {
    if (!current) return {};

    const now = moment().add(31, "minutes");
    const isToday = current.isSame(now, "day");

    if (isToday) {
      const currentHour = now.hour(); // Current hour in 24-hour format
      const currentMinute = now.minute();
      const isPM = currentHour >= 12;

      return {
        disabledHours: () => {
          // Disable all hours in the past
          const disabledHours = Array.from({ length: 24 }, (_, i) => i).filter((hour) => hour < currentHour);
          return disabledHours;
        },
        disabledMinutes: (selectedHour: number) => {
          // Map 12-hour format to 24-hour format
          const hour24 = selectedHour + (selectedHour < 12 && isPM ? 12 : 0);

          if (hour24 < currentHour) {
            // Disable all minutes for past hours
            return Array.from({ length: 60 }, (_, i) => i);
          } else if (hour24 === currentHour) {
            // Disable past minutes for the current hour
            return Array.from({ length: currentMinute }, (_, i) => i);
          }
          return [];
        }
      };
    }

    return {};
  };

  const disableToMaintenanceTime = () => {
    const fromTime = form.getFieldValue("from");
    if (!fromTime) return {};

    const fromHour = moment(fromTime, "HH:mm").hour();
    const fromMinute = moment(fromTime, "HH:mm").minute();

    return {
      disabledHours: () => Array.from({ length: fromHour + 1 }, (_, i) => i),
      disabledMinutes: (selectedHour: number) => (selectedHour === fromHour ? Array.from({ length: fromMinute + 1 }, (_, i) => i) : [])
    };
  };

  const validateSelectedDate = (notificationList: any) => (_: any, value: any) => {
    const selectedNotificationId = form.getFieldValue("notification_id");
    const selectedNotification = notificationList.find((item: { id: any }) => item.id === selectedNotificationId);

    if (selectedNotification?.notification_type === NotificationTypeConstants.MAINTENANCE && !value) {
      return Promise.reject(new Error("Please select a date."));
    }

    return Promise.resolve();
  };

  const validateTimeRange = (notificationList: any) => (_: any, value: any) => {
    const selectedNotificationId = form.getFieldValue("notification_id");
    const selectedNotification = notificationList.find((item: { id: any }) => item.id === selectedNotificationId);

    if (selectedNotification?.notification_type === NotificationTypeConstants.MAINTENANCE && (!value || !value[0] || !value[1])) {
      return Promise.reject(new Error("Please select a valid time range."));
    }

    return Promise.resolve();
  };

  /* EFFECTS */
  useEffect(() => {
    if (!isMaintenanceSelected) return;

    const notificationItem = notificationList?.find((record) => record?.id === MessagingSetOptions.MAINTENANCE);
    let notificationMessage = notificationItem?.notification_body;

    for (const [key, value] of Object.entries(maintenanceNotification)) {
      notificationMessage = notificationMessage ? notificationMessage?.replace(key, value) : "";
      form.setFieldValue("notification_message", notificationMessage);
    }

    // eslint-disable-next-line
  }, [isMaintenanceSelected, maintenanceNotification, masterFilters?.customerId, masterFilters?.locationId]);

  const updateMaintenanceMessage = () => {
    const customerLocation = locationList?.find((location) => location.id === masterFilters?.locationId);

    if (!customerLocation) return;

    setMaintenanceNotification((prevState) => {
      return { ...prevState, [MessagingTemplateConstants.CLOC]: customerLocation?.location_name };
    });
  };

  useEffect(() => {
    updateMaintenanceMessage();
    // eslint-disable-next-line
  }, [masterFilters?.customerId, masterFilters?.locationId]);

  /* HANDLERS */
  const handleDateChange = (date: Moment | null) => {
    const formattedDate = date ? date.format("DD-MM-YYYY") : MessagingTemplateConstants.DATE;
    setMaintenanceNotification((prevState) => {
      return { ...prevState, [MessagingTemplateConstants.DATE]: formattedDate };
    });
  };

  const handleTimeRangeChange: RangePickerProps["onChange"] = (values) => {
    const [fromTime, toTime] = values || [];
    const formattedFromTime = fromTime?.format("hh:mm a") || MessagingTemplateConstants.FROM;
    const formattedToTime = toTime?.format("hh:mm a") || MessagingTemplateConstants.TO;

    setMaintenanceNotification((prevState) => ({
      ...prevState,
      [MessagingTemplateConstants.FROM]: formattedFromTime,
      [MessagingTemplateConstants.TO]: formattedToTime
    }));
  };

  const handleChangeNotificationType = (value: CheckboxValueType[]) => {
    if (isSMSSelected) form.setFieldValue("mobile_numbers", null);
    setIsPUSHSelected(value?.includes(notificationEndpointOptions.PUSH));
    setIsSMSSelected(value?.includes(notificationEndpointOptions.SMS));
  };
  const handleBack = () => {
    navigate(-1);
  };

  const handleChangeScheduleType = (e: RadioChangeEvent) => {
    setScheduleType(e.target.value);
  };

  const handleChangeNotification = (value: number) => {
    setMaintenanceNotification({});
    form.resetFields(["notification_message", "selected_date", "time_range"]);
    updateMaintenanceMessage();

    const result = notificationList?.find((record) => record?.id === value);
    form.setFieldValue("notification_message", result?.notification_body ?? "");

    /* checks if maintenance notification is selected. */
    if (value === MessagingSetOptions.MAINTENANCE) handleMaintenanceSelected(true);
    else handleMaintenanceSelected(false);
  };

  const encryptMobileNumbers = (numbers: string) => {
    return numbers
      .split(",")
      .map((num) => num.trim())
      .filter((num) => /^\d{10}$/.test(num))
      .map((num) => RSAEncryption.encrypt(`+91${num}`));
  };

  const handleSubmit = async (data: SubmitDataProps) => {
    try {
      handleEnableSaving();

      if (!data?.selected_user?.length && !data?.mobile_numbers?.length) {
        CustomNotification.showErrorMessage("Required: Select User or Mobile Numbers.");
        return;
      }

      const dataToSendObject = {
        customer_id: masterFilters?.customerId!,
        customer_location_id: masterFilters?.locationId!,
        notification_id: data?.notification_id,
        send_to_web_user: data?.selected_user?.includes(userOptions.WEB_USER) ? true : false,
        send_to_mobile_user: data?.selected_user?.includes(userOptions.MOBILE_USER) ? true : false,
        notification_message: data?.notification_message?.trim(),
        mobile_number_list: data?.mobile_numbers ? encryptMobileNumbers(data?.mobile_numbers) : null,
        publish_on: scheduleType === scheduleOptions?.SCHEDULE_NOW ? moment().format("YYYY-MM-DD HH:mm") : data?.publish_on ? moment(data.publish_on).format("YYYY-MM-DD HH:mm") : null,
        isPublishNow: scheduleType === scheduleOptions?.SCHEDULE_NOW ? true : false,
        notification_endpoint_type: data?.notification_type?.join(", ") || ""
      };

      const response = await maintenanceNotificationService.createMaintenanceNotification(dataToSendObject);

      CustomNotification.showSuccessMessage(response?.message);
      form.resetFields();

      /*  Redirect list page */
      navigate(`${RouteConstants.MESSAGING}`);
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    } finally {
      handleDisableSaving();
    }
  };

  return {
    form,
    userOptions: Object.values(userOptions),
    notificationList,
    handleChangeNotification,
    loading: notificationListLoading,
    saving,
    handleSubmit,
    disabledDate,
    handleBack,
    scheduleOptions: Object.values(scheduleOptions),
    notificationEndpointOptions: Object.values(notificationEndpointOptions),
    handleChangeScheduleType,
    scheduleType,
    customerId: masterFilters?.customerId,
    locationId: masterFilters?.locationId,
    handleChangeNotificationType,
    isPUSHSelected,
    isSMSSelected,
    disabledTime,
    isMaintenanceSelected,
    handleDateChange,
    disableToMaintenanceTime,
    handleTimeRangeChange,
    validateSelectedDate,
    validateTimeRange
  };
};
